import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, X, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import SEO from '../components/SEO';

const DEFAULT_CATEGORIES = [
  { id: '', label: 'All' },
];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [loading, setLoading]     = useState(true);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hero, setHero]           = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const category = searchParams.get('category') || '';
  const search   = searchParams.get('search')   || '';
  const page     = Number(searchParams.get('page') || 1);
  const fromSolutions = searchParams.get('from') === 'solutions';

  // Sync input with URL param
  useEffect(() => { setSearchInput(search); }, [search]);

  // Fetch categories from DB
  useEffect(() => {
    api.get('/categories')
      .then(res => {
        const dbCats = res.data.categories || [];
        setCategories([{ id: '', label: 'All' }, ...dbCats]);
      })
      .catch(() => {});
  }, []);

  // Fetch hero content
  useEffect(() => {
    api.get('/content?page=products&section=hero')
      .then(res => {
        const items = res.data.content || [];
        if (items.length) setHero(items[0]);
      })
      .catch(() => {})
      .finally(() => setHeroLoaded(true));
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (category) params.category = category;
      if (search)   params.search   = search;
      const res = await api.get('/products', { params });
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [category, search, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const searchParamsRef = useRef(searchParams);
  useEffect(() => { searchParamsRef.current = searchParams; }, [searchParams]);

  const setParam = useCallback((key, val) => {
    const p = new URLSearchParams(searchParamsRef.current);
    if (val) p.set(key, val); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  }, [setSearchParams]);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const p = new URLSearchParams(searchParamsRef.current);
      const trimmed = val.trim();
      if (trimmed) p.set('search', trimmed); else p.delete('search');
      p.delete('page');
      setSearchParams(p);
    }, 350);

    if (val.trim().length >= 1) {
      const cat = new URLSearchParams(searchParamsRef.current).get('category') || '';
      api.get('/products/suggestions', { params: { q: val.trim(), ...(cat && { category: cat }) } })
        .then(res => {
          setSuggestions(res.data.suggestions || []);
          setShowSuggestions(true);
        })
        .catch(() => setSuggestions([]));
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (name) => {
    setSearchInput(name);
    setSuggestions([]);
    setShowSuggestions(false);
    clearTimeout(debounceRef.current);
    const p = new URLSearchParams(searchParamsRef.current);
    p.set('search', name);
    p.delete('page');
    setSearchParams(p);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    clearTimeout(debounceRef.current);
    const p = new URLSearchParams(searchParamsRef.current);
    p.delete('search');
    p.delete('page');
    setSearchParams(p);
  };

  return (
    <>
      <SEO title="Products" description="Browse our premium sample products." />

      <section className="relative h-[432px] flex items-end pb-16 overflow-hidden">
        {heroLoaded && (
          <img
            src={hero?.images?.[0] || hero?.image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80'}
            alt="Products"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: hero?.extraData?.objectPosition || '50% 50%' }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <p className="text-xs tracking-[0.4em] uppercase mb-2"
            style={{ color: hero?.extraData?.subtitleColor || '#B8A830' }}>
            {hero?.subtitle || 'Our Catalogue'}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl"
            style={{ color: hero?.extraData?.titleColor || '#FFFFFF' }}>
            {hero?.title || 'Products'}
          </h1>
        </div>
      </section>

      <section className="py-16 px-6 max-w-7xl mx-auto">
        {/* Back to Solutions button */}
        {fromSolutions && (
          <div className="mb-8">
            <Link to="/solutions"
              className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-gold transition-colors duration-200">
              <ArrowLeft size={16} />
              Back to Solutions
            </Link>
          </div>
        )}

        {/* Active category banner */}
        {fromSolutions && category && (
          <div className="mb-6 px-4 py-3 bg-gold/10 border border-gold/30 rounded-lg inline-flex items-center gap-2">
            <span className="text-xs text-amber-700 font-medium uppercase tracking-wider">
              Browsing: {category.replace(/-/g, ' ')}
            </span>
            <button onClick={() => setParam('category', '')}
              className="text-amber-500 hover:text-amber-700 ml-2">
              <X size={12} />
            </button>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6 mb-12 items-start md:items-center justify-between">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setParam('category', cat.id)}
                className={`px-5 py-2 text-xs tracking-widest uppercase border transition-colors duration-200
                  ${category === cat.id ? 'bg-gold text-white border-gold' : 'border-dark-600 text-ink-600 hover:border-gold hover:text-gold'}`}>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Live Search with suggestions */}
          <div className="relative" ref={wrapperRef}>
            <div className="relative flex items-center">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchInput}
                onChange={handleSearchChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onKeyDown={e => e.key === 'Escape' && setShowSuggestions(false)}
                className="bg-white border border-dark-600 text-ink pl-10 pr-8 py-2 text-sm focus:outline-none focus:border-gold w-64 transition-colors"
              />
              {searchInput && (
                <button onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-50 top-full left-0 w-full bg-white border border-gray-200 shadow-lg mt-1 max-h-60 overflow-y-auto">
                {suggestions.map(s => (
                  <li key={s._id}
                    onMouseDown={() => handleSuggestionClick(s.name)}
                    className="px-4 py-2.5 text-sm text-ink cursor-pointer hover:bg-amber-50 hover:text-gold flex items-center gap-2 border-b border-gray-100 last:border-0">
                    <Search size={12} className="text-gray-400 shrink-0" />
                    <span>{s.name}</span>
                    {s.category && (
                      <span className="ml-auto text-xs text-gray-400 capitalize">{s.category.replace(/-/g, ' ')}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <p className="text-ink-400 text-sm mb-8">{total} product{total !== 1 ? 's' : ''} found</p>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-dark-800 animate-pulse aspect-square" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 text-ink-400">No products found.</div>
        ) : (
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(p => <ProductCard key={p._id} product={p} />)}
          </motion.div>
        )}

        <Pagination page={page} pages={pages}
          onPage={p => { const params = new URLSearchParams(searchParams); params.set('page', p); setSearchParams(params); }} />
      </section>
    </>
  );
}
